import firebase from "firebase/compat/app";
import app from "../firebase";
import "firebase/firestore";
import "firebase/messaging";
import "firebase/compat/storage";

import {
        createCacheKey,
        fetchWithCache,
        invalidateCache,
        DEFAULT_CACHE_TTL_MS,
} from "../utils/firestoreCache";
import * as treatmentService from "../api/firestore/services/treatmentService";
import { isAnimalActive } from "../utils/animalUtils";

const db = firebase.firestore(app);

const CACHE_NAMESPACES = {
        PREDICTIONS: "dbPredictions",
        REGISTERED_TOKENS: "dbRegisteredTokens",
        WORKERS: "dbWorkers",
        WORK_ITEMS: "workItems",
        EVENTS: "dbEvents",
        WORK_HISTORY: "dbWorkHistory",
};

const DEFAULT_DB_CACHE_MS = DEFAULT_CACHE_TTL_MS;

const resolveCacheArgs = (input) => {
        if (typeof input === "boolean") {
                return { forceRefresh: input, cacheDurationMs: undefined };
        }

        if (input && typeof input === "object") {
                const { forceRefresh = false, cacheDurationMs } = input;
                return {
                        forceRefresh: Boolean(forceRefresh),
                        cacheDurationMs:
                                typeof cacheDurationMs === "number" && !Number.isNaN(cacheDurationMs)
                                        ? cacheDurationMs
                                        : undefined,
                };
        }

	return { forceRefresh: false, cacheDurationMs: undefined };
};


const invalidateTokenCaches = (userId) => {
        if (!userId) {
                return;
        }

	invalidateCache(`${CACHE_NAMESPACES.REGISTERED_TOKENS}:${userId}`);
	invalidateCache(`${CACHE_NAMESPACES.WORKERS}:${userId}`);
};

/**
 * Fetches all registered animals for a given user
 * @deprecated Use animalService.getAnimals instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array>} Array of animal objects with normalized data
 * @path Users/{userID}/Animals
 */
export async function getAnimals(userID) {
	console.warn('[firestore][getAnimals] - DEPRECATED: Use animalService.getAnimals instead');
	const { getAnimals } = await import('../api/firestore/services/animalService');
	return getAnimals(userID);
}

/**
 * Retrieves metadata for specified nodes
 * @deprecated Use sensorService.getMetaDataOfNodes instead
 * @param {string} userID - The unique identifier for the user
 * @param {Array<{Group: string}>} nodes - Array of node objects to fetch metadata for
 * @returns {Promise<Array<{t: number, cMean: number, aMean: number}>>} Array of metadata objects with timestamp and sensor readings
 * @path Users/{userID}/Meta/{node.Group}/Sensordata/Day
 */
export async function getMetaDataOfNodes(userID, nodes) {
	console.warn('[firestore][getMetaDataOfNodes] - DEPRECATED: Use sensorService.getMetaDataOfNodes instead');
	const { getMetaDataOfNodes } = await import('../api/firestore/services/sensorService');
	return getMetaDataOfNodes(userID, nodes);
}

/**
 * Fetches AI status settings for a user
 * @deprecated Use deviceService.getAIStatus instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<{Birth: number, Heat: number, Health: number}>} Object containing Birth, Heat and Health status values
 * @path Users/{userID}/Account/AI
 */
export async function getAIStatus(userID) {
	console.warn('[firestore][getAIStatus] - DEPRECATED: Use deviceService.getAIStatus instead');
	const { getAIStatus } = await import('../api/firestore/services/deviceService');
	return getAIStatus(userID);
}

/**
 * Retrieves all alerts for a user
 * @deprecated Use alertService.getAlerts instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<{ID: string, [key: string]: any}>>} Array of alert objects with IDs
 * @path Users/{userID}/Alerts
 */
export async function getAlerts(userID) {
	console.warn('[firestore][getAlerts] - DEPRECATED: Use alertService.getAlerts instead');
	const { getAlerts } = await import('../api/firestore/services/alertService');
	return getAlerts(userID);
}

/**
 * Deletes a specific alert
 * @deprecated Use alertService.deleteAlert instead
 * @param {string} userID - The unique identifier for the user
 * @param {string} alertID - The ID of the alert to delete
 * @returns {Promise<void>}
 * @path Users/{userID}/Alerts/{alertID}
 */
export async function deleteAlert(userID, alertID) {
	console.warn('[firestore][deleteAlert] - DEPRECATED: Use alertService.deleteAlert instead');
	const { deleteAlert } = await import('../api/firestore/services/alertService');
	return deleteAlert(userID, alertID);
}

/**
 * Gets custom temperature thresholds for a user
 * @deprecated Use alertService.getCustomTemperatureThresholds instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Object>} Temperature threshold settings
 * @path Users/{userID}/Account/Thresholds
 */
export async function getCustomTemperatureThresholds(userID) {
	console.warn('[firestore][getCustomTemperatureThresholds] - DEPRECATED: Use alertService.getCustomTemperatureThresholds instead');
	const { getCustomTemperatureThresholds } = await import('../api/firestore/services/alertService');
	return getCustomTemperatureThresholds(userID);
}

/**
 * Sets custom temperature thresholds for a user
 * @deprecated Use alertService.setCustomTemperatureThresholds instead
 * @param {string} userID - The unique identifier for the user
 * @param {Object} threshold - Temperature threshold settings to save
 * @returns {Promise<void>}
 * @path Users/{userID}/Account/Thresholds
 */
export async function setCustomTemperatureThresholds(userID, threshold) {
	console.warn('[firestore][setCustomTemperatureThresholds] - DEPRECATED: Use alertService.setCustomTemperatureThresholds instead');
	const { setCustomTemperatureThresholds } = await import('../api/firestore/services/alertService');
	return setCustomTemperatureThresholds(userID, threshold);
}

/**
 * Gets all registered animal IDs for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<string>>} Array of animal IDs
 * @path Users/{userID}/Animals
 */
export async function getRegisteredIDs(userID) {
	let ids = [];

	await db
		.collection("Users")
		.doc(userID)
		.collection("Animals")
		.get()
		.then((snapshot) => {
			snapshot.forEach((doc) => {
				ids.push(doc.data().ID);
			});
		})
		.catch((e) => {
			throw e;
		});

	return ids;
}

/**
 * Gets all archived animals for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of archived animal objects
 * @path Users/{userID}/ArchivedAnimals
 */
export async function getArchivedAnimals(userID) {
	// Get animals from both ArchivedAnimals (current) and Archive (legacy) collections
	const [archivedSnapshot, legacySnapshot] = await Promise.all([
		db
			.collection("Users")
			.doc(userID)
			.collection("ArchivedAnimals")
			.get()
			.catch((e) => {
				console.error("[firestore][getArchivedAnimals] - Error getting ArchivedAnimals:", e);
				return { docs: [] };
			}),
		db
			.collection("Users")
			.doc(userID)
			.collection("Archive")
			.get()
			.catch((e) => {
				console.error("[firestore][getArchivedAnimals] - Error getting Archive (legacy):", e);
				return { docs: [] };
			})
	]);

	// Combine documents from both collections, prioritizing ArchivedAnimals
	const allDocs = [...archivedSnapshot.docs];
	
	// Add legacy Archive docs only if they don't exist in ArchivedAnimals (by MAC)
	const archivedMACs = new Set(archivedSnapshot.docs.map(doc => doc.id));
	legacySnapshot.docs.forEach(doc => {
		if (!archivedMACs.has(doc.id)) {
			allDocs.push(doc);
		}
	});

	const animalPromises = allDocs.map(async (animalDoc) => {
		let animal = animalDoc.data();

		if (animal.ID) {
			animal.Active = isAnimalActive(animal) ? "Sending" : "Inactive";
			animal.Group = animal.Group ? animal.Group : "-";
			animal.Location = animal.Location ? animal.Location : "-";

			return animal;
		} else {
			return null;
		}
	});

	const animals = (await Promise.all(animalPromises)).filter(
		(animal) => animal !== null
	);

	return animals;
}

/**
 * Sets a user account to inactive
 * @deprecated Use userService.setAccountInactive instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<void>}
 * @path Users/{userID}
 */
export async function setAccountInactive(userID) {
	console.warn('[firestore][setAccountInactive] - DEPRECATED: Use userService.setAccountInactive instead');
	const { setAccountInactive } = await import('../api/firestore/services/userService');
	return setAccountInactive(userID);
}

/**
 * Gets all animals in a specific group
 * @deprecated Use animalService.getAnimalsOfGroup instead
 * @param {string} userID - The unique identifier for the user
 * @param {string} group - The group identifier
 * @returns {Promise<Array<Object>>} Array of animal objects in the specified group
 * @path Users/{userID}/Animals
 */
export async function getAnimalsOfGroup(userID, group) {
	console.warn('[firestore][getAnimalsOfGroup] - DEPRECATED: Use animalService.getAnimalsOfGroup instead');
	const { getAnimalsOfGroup } = await import('../api/firestore/services/animalService');
	return getAnimalsOfGroup(userID, group);
}

/**
 * Sets an animal's status to inactive
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @returns {Promise<void>}
 * @path Users/{userID}/Animals/{mac}
 */
export async function setAnimalInactive(userID, mac) {
	await db
		.collection("Users")
		.doc(userID)
		.collection("Animals")
		.doc(mac)
		.update({
			Active: false,
		})
		.catch((e) => {
			throw e;
		});
	
	// Update farm size in billing after animal inactivation
	try {
		const { updateFarmSizeInBilling } = await import("../api/firestore/services/billingService");
		await updateFarmSizeInBilling(userID);
	} catch (error) {
		console.error("[firestore][setAnimalInactive] - Error updating farm size:", error);
		// Don't fail animal inactivation if billing update fails
	}
}

/**
 * Generates a placeholder MAC address for animals without physical eartags
 * Format: PLACEHOLDER:{userId}:{timestamp}:{random}
 * @param {string} userID - The unique identifier for the user
 * @returns {string} Placeholder MAC address
 */
/**
 * @deprecated Use animalService.generatePlaceholderMAC instead
 */
export function generatePlaceholderMAC(userID) {
	console.warn('[firestore][generatePlaceholderMAC] - DEPRECATED: Use animalService.generatePlaceholderMAC instead');
	const { generatePlaceholderMAC } = require('../api/firestore/services/animalService');
	return generatePlaceholderMAC(userID);
}

/**
 * Updates or creates an animal record
 * Validates tag availability and allows reuse of archived tags
 * @param {string} userID - The unique identifier for the user
 * @param {Object} animal - Animal data to save
 * @param {string} [animal.MAC] - MAC address of the animal's device (used as document ID). Auto-generated if not provided.
 * @returns {Promise<string>} The MAC address used (for the created animal)
 * @path Users/{userID}/Animals/{animal.MAC}
 */
/**
 * @deprecated Use animalService.setAnimal instead
 */
export async function setAnimal(userID, animal, ...args) {
	console.warn('[firestore][setAnimal] - DEPRECATED: Use animalService.setAnimal instead');
	const { setAnimal } = await import('../api/firestore/services/animalService');
	return setAnimal(userID, animal, ...args);
}

/**
 * Updates a specific field on an animal
 * @param {string} userID - The unique identifier for the user
 * @param {string} animalMAC - MAC address of the animal's device
 * @param {Object} updates - Object with field names and values to update
 * @returns {Promise<void>}
 * @path Users/{userID}/Animals/{animalMAC}
 */
/**
 * @deprecated Use animalService.updateAnimalFields instead
 */
export async function updateAnimalFields(userID, animalMAC, updates) {
	console.warn('[firestore][updateAnimalFields] - DEPRECATED: Use animalService.updateAnimalFields instead');
	const { updateAnimalFields } = await import('../api/firestore/services/animalService');
	return updateAnimalFields(userID, animalMAC, updates);
}

/**
 * Gets all predictions for a user
 * @deprecated Use predictionService.getAllPredictions instead
 * @param {string} userId - The unique identifier for the user
 * @param {boolean} onlyCurrent - Whether to fetch only current predictions
 * @returns {Promise<Array<Object>>} Array of prediction objects
 * @path Users/{userId}/Predictions
 * @path Users/{userId}/Predictions/{MAC}/Health
 * @path Users/{userId}/Predictions/{MAC}/Birth
 * @path Users/{userId}/Predictions/{MAC}/Heat
 */
export async function getAllPredictions(userId, options = false) {
	console.warn('[firestore][getAllPredictions] - DEPRECATED: Use predictionService.getAllPredictions instead');
	const { getAllPredictions } = await import('../api/firestore/services/predictionService');
	return getAllPredictions(userId, options);
}

/**
 * Gets current predictions for a user
 * @deprecated Use predictionService.getCurrentPredictions instead
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of current predictions
 * @path Users/{userId}/Predictions
 */
export async function getCurrentPredictions(userId) {
	console.warn('[firestore][getCurrentPredictions] - DEPRECATED: Use predictionService.getCurrentPredictions instead');
	const { getCurrentPredictions } = await import('../api/firestore/services/predictionService');
	return getCurrentPredictions(userId);
}

/**
 * Gets all custom labels for a user
 * @deprecated Use predictionService.getAllCustomLabels instead
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of custom label objects
 * @path Users/{userId}/Label
 */
export async function gettAllCustomLabels(userId) {
	console.warn('[firestore][gettAllCustomLabels] - DEPRECATED: Use predictionService.getAllCustomLabels instead');
	const { getAllCustomLabels } = await import('../api/firestore/services/predictionService');
	return getAllCustomLabels(userId);
}

/**
 * Writes a new label for a prediction
 * @deprecated Use predictionService.writeNewLabel instead
 * @param {string} userId - The unique identifier for the user
 * @param {Object} prediction - The prediction data to save as a label
 * @param {string} prediction.ExactTime - Timestamp used in document ID
 * @param {string} prediction.MAC - MAC address used in document ID
 * @returns {Promise<void>}
 * @path Users/{userId}/Label/{prediction.ExactTime}_{prediction.MAC}
 */
export async function writeNewLabel(userId, prediction) {
	console.warn('[firestore][writeNewLabel] - DEPRECATED: Use predictionService.writeNewLabel instead');
	const { writeNewLabel } = await import('../api/firestore/services/predictionService');
	return writeNewLabel(userId, prediction);
}

/**
 * Updates the custom name for a device token
 * @param {string} uid - The unique identifier for the user
 * @param {string} Token - The FCM token to update
 * @param {string} newCustomName - The new custom name to set
 * @returns {Promise<void>}
 * @path Users/{uid}
 */
export async function updateCustomName(uid, Token, newCustomName) {
        try {
                const userRef = db.collection("Users").doc(uid);
                const doc = await userRef.get();

                if (doc.exists) {
                        const tokens = doc.data().FCMTokens || [];
                        const index = tokens.findIndex((token) => token.Token === Token);

                        if (index !== -1) {
                                tokens[index].CustomName = newCustomName;
                                await userRef.update({
                                        FCMTokens: tokens,
                                });
                        }
                }

                invalidateTokenCaches(uid);
        } catch (error) {
                throw error;
        }
}

/**
 * Updates the notification value for a device token
 * @param {string} uid - The unique identifier for the user
 * @param {string} Token - The FCM token to update
 * @param {boolean} newNotificationValue - The new notification value
 * @returns {Promise<void>}
 * @path Users/{uid}
 */
export async function updateNotificationValue(
        uid,
        Token,
        newNotificationValue
) {
        try {
                const userRef = db.collection("Users").doc(uid);
                const userDoc = await userRef.get();

                if (userDoc.exists) {
                        const tokens = userDoc.data().FCMTokens || [];
                        const index = tokens.findIndex((token) => token.Token === Token);

                        if (index !== -1) {
                                tokens[index].Notification = newNotificationValue;
                                await userRef.update({
                                        FCMTokens: tokens,
                                });
                        }
                }

                invalidateTokenCaches(uid);
        } catch (error) {
                throw error;
        }
}

/**
 * Deletes a label
 * @deprecated Use predictionService.deleteLabel instead
 * @param {string} userId - The unique identifier for the user
 * @param {Object} prediction - The prediction data to delete
 * @param {string} prediction.ExactTime - Timestamp used in document ID
 * @param {string} prediction.MAC - MAC address used in document ID
 * @returns {Promise<void>}
 * @path Users/{userId}/Label/{prediction.ExactTime}_{prediction.MAC}
 */
export async function deleteLabel(userId, prediction) {
	console.warn('[firestore][deleteLabel] - DEPRECATED: Use predictionService.deleteLabel instead');
	const { deleteLabel } = await import('../api/firestore/services/predictionService');
	return deleteLabel(userId, prediction);
}

/**
 * Marks a prediction as labeled
 * @deprecated Use predictionService.markAsLabeled instead
 * @param {string} userId - The unique identifier for the user
 * @param {string} type - Type of prediction (Health, Birth, Heat)
 * @param {string|number} docId - Document ID of the prediction
 * @param {string} mac - MAC address of the animal's device
 * @returns {Promise<void>}
 * @path Users/{userId}/Predictions/{mac}/{type}/{docId}
 */
export async function markAsLabled(userId, type, docId, mac) {
	console.warn('[firestore][markAsLabled] - DEPRECATED: Use predictionService.markAsLabeled instead');
	const { markAsLabeled } = await import('../api/firestore/services/predictionService');
	return markAsLabeled(userId, type, docId, mac);
}

/**
 */
export async function getEnviromentDataOfGroup(userID, nodes) {
	let data = [];

        if (!nodes || !Array.isArray(nodes) || nodes.length === 0) {
                return [];
        }

	try {
		const promises = nodes.map(async (node) => {
                        if (!node) {
                                return Promise.resolve();
                        }

			// Use appropriate ID field, with fallbacks
			// Try UID first, then Id (lowercase), then ID (uppercase)
			const nodeId = node.Id;

                        if (!nodeId) {
                                return Promise.resolve();
                        }

			return db
				.collection("Users")
				.doc(userID)
				.collection("Nodes")
				.doc(nodeId)
				.collection("Enviroment")
				.doc("Sensordata")
				.get()
				.then((snapshot) => {
                                        const sensordata = snapshot.data();

					if (sensordata && sensordata.Data && Array.isArray(sensordata.Data)) {
						sensordata.Data.forEach((dataPoint) => {
							if (dataPoint && dataPoint.t) {
								data.push({
									t: dataPoint.t,
									Humidity: dataPoint.Humidity || 0,
									Temperature: dataPoint.Temperature || 0,
								});
							}
						});
                                        }
				})
				.catch((error) => {
					console.error("Error fetching data for node", nodeId, ":", error);
					// Don't throw, just log the error to prevent Promise.all from failing
				});
		});

		await Promise.all(promises);
	} catch (error) {
		console.error("Error in getEnviromentDataOfGroup:", error);
		// Return empty array instead of throwing
		return [];
	}

	// Only process if we have data
        if (data.length === 0) {
                return [];
        }

	// Sort data by timestamp
	data.sort((a, b) => a.t - b.t);

	const runningMeanData = [];
	for (let i = 0; i < data.length; i++) {
		if (i >= 4) {
			let sumHumidity = 0;
			let sumTemperature = 0;
			for (let j = i - 4; j <= i; j++) {
				sumHumidity += isNaN(+data[j].Humidity) ? 0 : +data[j].Humidity;
				sumTemperature += isNaN(+data[j].Temperature)
					? 0
					: +data[j].Temperature;
			}
			runningMeanData.push({
				t: data[i].t,
				Humidity: sumHumidity / 5,
				Temperature: sumTemperature / 5,
			});
		}
	}

	return runningMeanData;
}

/**
 * Archives an animal (moves it from Animals to ArchivedAnimals)
 * @deprecated Use animalService.archiveAnimal instead
 * @param {string} userId - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @param {Function} [onProgress] - Optional progress callback: (current, total, message) => void
 * @param {string} [reason] - Optional archive reason for backward compatibility
 * @returns {Promise<void>}
 * @path Users/{userId}/Animals/{mac}
 * @path Users/{userId}/ArchivedAnimals/{mac}
 */
export async function archiveAnimal(userId, mac, onProgress = null, reason = null) {
	console.warn('[firestore][archiveAnimal] - DEPRECATED: Use animalService.archiveAnimal instead');
	const { archiveAnimal } = await import('../api/firestore/services/animalService');
	return archiveAnimal(userId, mac, onProgress, reason);
}

/**
 * Reactivates an archived animal (moves it from ArchivedAnimals to Animals)
 * @deprecated Use animalService.reactivateAnimal instead
 * @param {string} userId - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @param {Function} [onProgress] - Optional progress callback: (current, total, message) => void
 * @returns {Promise<void>}
 * @path Users/{userId}/ArchivedAnimals/{mac}
 * @path Users/{userId}/Animals/{mac}
 */
export async function reactivateAnimal(userId, mac, onProgress = null) {
	console.warn('[firestore][reactivateAnimal] - DEPRECATED: Use animalService.reactivateAnimal instead');
	const { reactivateAnimal } = await import('../api/firestore/services/animalService');
	return reactivateAnimal(userId, mac, onProgress);
}

/**
 * Gets user data
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<{Email: string, Name: string, Phone: string, Language: string, WedaIntegration: boolean, IsResearch: boolean, Type: string}>} User data
 * @path Users/{userID}
 */
export async function getUserData(userID) {
        let user = {
                Email: "",
                Name: "",
                Phone: "",
                Language: "en",
                WedaIntegration: false,
                IsResearch: false,
                Type: "pig",
                Role: "user",
                betaUser: false,
                ProfileComplete: false,
                Devices: [],
        };

	try {
		const documentSnapshot = await db.collection("Users").doc(userID).get();

		if (documentSnapshot.exists) {
			const user_ = documentSnapshot.data();

			// Only update the default values if the document exists and has data
			if (user_) {
                                user = {
                                        Email: user_.Email || "",
                                        Name: user_.Name || "",
                                        Phone: user_.Phone || "",
                                        Language: user_.Language || "en",
                                        WedaIntegration:
                                                user_.WedaIntegration !== undefined ? user_.WedaIntegration : false,
                                        IsResearch: user_.IsResearch !== undefined ? user_.IsResearch : false,
                                        Type: user_.Type || "pig",
                                        Role: user_.Role || "user", // Include role if present
                                        betaUser: user_.betaUser !== undefined ? user_.betaUser : false,
                                        ProfileComplete:
                                                user_.ProfileComplete !== undefined
                                                        ? Boolean(user_.ProfileComplete)
                                                        : false,
                                        Devices: Array.isArray(user_.Devices) ? user_.Devices : [],
                                        WelcomeDialogSeenVersions: Array.isArray(user_.WelcomeDialogSeenVersions)
                                                ? user_.WelcomeDialogSeenVersions
                                                : [],
                                        AccountType: user_.AccountType || "full", // saas | biotag | full
                                };
                        }
                }
		// If document doesn't exist, return the default user object initialized above
	} catch (e) {
		console.error("Error fetching user data:", e);
		throw e;
	}

	return user;
}

/**
 * Gets all FCM tokens for a user
 * @deprecated Use workerService.getTokens instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<{Token: string, Name: string, CustomName: string, UID: string, Rank: number, Notification: boolean, isActive: boolean}>>} Array of token objects
 * @path Users/{userID}
 */
export async function getTokens(userID) {
	console.warn('[firestore][getTokens] - DEPRECATED: Use workerService.getTokens instead');
	const { getTokens } = await import('../api/firestore/services/workerService');
	return getTokens(userID);
}

/**
 * Gets registered FCM tokens for a user (tokens with custom names)
 * @deprecated Use workerService.getRegisteredTokens instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<{Token: string, Name: string, CustomName: string, UID: string, Rank: number, Notification: boolean, isActive: boolean}>>} Array of registered token objects
 * @path Users/{userID}
 */
export async function getRegisteredTokens(userID, options = {}) {
	console.warn('[firestore][getRegisteredTokens] - DEPRECATED: Use workerService.getRegisteredTokens instead');
	const { getRegisteredTokens } = await import('../api/firestore/services/workerService');
	return getRegisteredTokens(userID, options);
}

/**
 * Updates user data
 * @deprecated Use userService.updateUserData instead
 * @param {string} userID - The unique identifier for the user
 * @param {Object} userData - User data to update
 * @returns {Promise<void>}
 * @path Users/{userID}
 */
export async function updateUserData(userID, userData) {
	console.warn('[firestore][updateUserData] - DEPRECATED: Use userService.updateUserData instead');
	const { updateUserData } = await import('../api/firestore/services/userService');
	return updateUserData(userID, userData);
}

/**
 * Marks a welcome dialog version as seen for a user
 * @deprecated Use userService.updateUserWelcomeVersion instead
 * @param {string} userID - The unique identifier for the user
 * @param {string} version - The version string (e.g., "2.0")
 * @returns {Promise<void>}
 * @path Users/{userID}
 */
export async function updateUserWelcomeVersion(userID, version) {
	console.warn('[firestore][updateUserWelcomeVersion] - DEPRECATED: Use userService.updateUserWelcomeVersion instead');
	const { updateUserWelcomeVersion } = await import('../api/firestore/services/userService');
	return updateUserWelcomeVersion(userID, version);
}

/**
 * Gets user notification settings
 * @deprecated Use userService.getUserNotificationData instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Object>} Notification settings
 * @path Users/{userID}/Account/Notification
 */
export async function getUserNotificationData(userID) {
	console.warn('[firestore][getUserNotificationData] - DEPRECATED: Use userService.getUserNotificationData instead');
	const { getUserNotificationData } = await import('../api/firestore/services/userService');
	return getUserNotificationData(userID);
}

/**
 * Gets work history for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<{startDate: Date, title: string, type: string, endDate?: Date, notes?: string}>>} Array of work history entries
 * @path Users/{userId}/WorkHistory
 * @path Users/{userId}/Treatments
 */
export async function getWorkHistory(userId, cacheOptions) {
	const { forceRefresh, cacheDurationMs } = resolveCacheArgs(cacheOptions);
	const cacheKey = createCacheKey(CACHE_NAMESPACES.WORK_HISTORY, userId);
	const WORK_HISTORY_CACHE_MS = 45_000; // 45 seconds (shorter for frequently updated work items)

	return fetchWithCache(
		cacheKey,
		async () => {
			try {
				const workHistorySnapshot = await db
					.collection("Users")
					.doc(userId)
					.collection("WorkHistory")
					.get();

				if (workHistorySnapshot.empty) {
					return [];
				}

				const workHistory = [];
				workHistorySnapshot.forEach((doc) => {
					const data = doc.data();
					const id = doc.id;
					const item = { id, ...data };
					workHistory.push(item);
				});

				return workHistory;
			} catch (error) {
				console.error("Error getting work history:", error);
				throw error;
			}
		},
		{ ttlMs: cacheDurationMs ?? WORK_HISTORY_CACHE_MS, forceRefresh }
	);
}

/**
 * Creates a new work task in the work history
 * @deprecated Use userService.setWorkHistory instead
 * @param {string} userId - The unique identifier for the user
 * @param {Object} task - The task data
 * @returns {Promise<string>} ID of the created task
 * @path Users/{userId}/WorkHistory/{taskId}
 */
export async function setWorkHistory(userId, task) {
	console.warn('[firestore][setWorkHistory] - DEPRECATED: Use userService.setWorkHistory instead');
	const { setWorkHistory } = await import('../api/firestore/services/userService');
	return setWorkHistory(userId, task);
}

/**
 * Updates the status of a work task
 * @deprecated Use userService.updateWorkTaskStatus instead
 * @param {string} userId - The unique identifier for the user
 * @param {string} taskId - The task ID to update
 * @param {string} status - The new status (pending, in-progress, completed, cancelled)
 * @returns {Promise<void>}
 * @path Users/{userId}/WorkHistory/{taskId}
 */
export async function updateWorkTaskStatus(userId, taskId, status) {
	console.warn('[firestore][updateWorkTaskStatus] - DEPRECATED: Use userService.updateWorkTaskStatus instead');
	const { updateWorkTaskStatus } = await import('../api/firestore/services/userService');
	return updateWorkTaskStatus(userId, taskId, status);
}

/**
 * Gets workers information (custom named FCM tokens that can be assigned tasks)
 * @deprecated Use workerService.getWorkers instead
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<{id: string, name: string, token: string, isActive: boolean, lastActivity: number|null}>>} Array of worker objects
 * @path Users/{userId}
 */
export async function getWorkers(userId, options = {}) {
	console.warn('[firestore][getWorkers] - DEPRECATED: Use workerService.getWorkers instead');
	const { getWorkers } = await import('../api/firestore/services/workerService');
	return getWorkers(userId, options);
}

/**
 * Sets user notification settings
 * @deprecated Use userService.setUserNotificationData instead
 * @param {string} userID - The unique identifier for the user
 * @param {Object} notificationData - Notification settings to save
 * @returns {Promise<void>}
 * @path Users/{userID}/Account/Notification
 */
export async function setUserNotificationData(userID, notificationData) {
	console.warn('[firestore][setUserNotificationData] - DEPRECATED: Use userService.setUserNotificationData instead');
	const { setUserNotificationData } = await import('../api/firestore/services/userService');
	return setUserNotificationData(userID, notificationData);
}

/**
 * Gets sensitivity settings for a user
 * @deprecated Use userService.getUserSensitivitySettings instead
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<{Temperature: number, Activity: number, Birth: number, Heat: number}>} Sensitivity settings
 * @path Users/{userId}/Account/Sensitivity
 */
export async function getUserSensitivitySettings(userId) {
	console.warn('[firestore][getUserSensitivitySettings] - DEPRECATED: Use userService.getUserSensitivitySettings instead');
	const { getUserSensitivitySettings } = await import('../api/firestore/services/userService');
	return getUserSensitivitySettings(userId);
}

/**
 * Sets sensitivity settings for a user
 * @deprecated Use userService.setUserSensitivitySettings instead
 * @param {string} userId - The unique identifier for the user
 * @param {Object} sensitivityData - Sensitivity settings to save
 * @returns {Promise<void>}
 * @path Users/{userId}/Account/Sensitivity
 */
export async function setUserSensitivitySettings(userId, sensitivityData) {
	console.warn('[firestore][setUserSensitivitySettings] - DEPRECATED: Use userService.setUserSensitivitySettings instead');
	const { setUserSensitivitySettings } = await import('../api/firestore/services/userService');
	return setUserSensitivitySettings(userId, sensitivityData);
}

/**
 * Gets billing data for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Object>} Billing data
 * @path Users/{userID}/Account/Billing
 */
export async function getUserBillingData(userID) {
	let billing;

	await db
		.collection("Users")
		.doc(userID)
		.collection("Account")
		.doc("Billing")
		.get()
		.then((documentSnapshot) => {
			billing = documentSnapshot.data();
		})
		.catch((e) => {
			throw e;
		});

	return billing;
}

/**
 * Gets all events for a user
 * @param {string} userID - The unique identifier for the user
 * @param {number} [from=0] - Timestamp to filter events from
 * @returns {Promise<Array<Object>>} Array of event objects
 * @path Users/{userID}/Events
 */
export async function getAllEvents(userID, from = 0, cacheOptions) {
	const { forceRefresh, cacheDurationMs } = resolveCacheArgs(cacheOptions);
	const cacheKey = createCacheKey(CACHE_NAMESPACES.EVENTS, userID, from);

	return fetchWithCache(
		cacheKey,
		async () => {
			const events = [];
			const snapshot = await db
				.collection("Users")
				.doc(userID)
				.collection("Events")
				.where("t", ">=", from)
				.get();

			snapshot.forEach((doc) => {
				const data = doc.data();
				data.ID = doc.id;
				events.push(data);
			});
			return events;
		},
		{ ttlMs: cacheDurationMs ?? DEFAULT_DB_CACHE_MS, forceRefresh }
	);
}

/**
 * Gets current events for a user
 * @deprecated Use eventService.getCurrentEvents instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of current event objects
 * @path Users/{userID}/Predictions
 * @path Users/{userID}/Events
 */
export async function getCurrentEvents(userID) {
	console.warn('[firestore][getCurrentEvents] - DEPRECATED: Use eventService.getCurrentEvents instead');
	const { getCurrentEvents } = await import('../api/firestore/services/eventService');
	return getCurrentEvents(userID);
}

/**
 * Deletes a device token
 * @deprecated Use deviceService.deleteDevice instead
 * @param {string} userID - The unique identifier for the user
 * @param {string} deviceID - The device token to delete
 * @returns {Promise<void>}
 * @path Users/{userID}
 */
export async function deleteDevice(userID, deviceID) {
	console.warn('[firestore][deleteDevice] - DEPRECATED: Use deviceService.deleteDevice instead');
	const { deleteDevice } = await import('../api/firestore/services/deviceService');
	return deleteDevice(userID, deviceID);
}

/**
 * Gets specific events for an animal
 * @deprecated Use eventService.getSpecificEventOf instead
 * @param {string} animal - MAC address of the animal's device
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of event objects for the animal
 * @path Users/{userID}/Events
 */
export async function getSpecificEventOf(animal, userID) {
	console.warn('[firestore][getSpecificEventOf] - DEPRECATED: Use eventService.getSpecificEventOf instead');
	const { getSpecificEventOf } = await import('../api/firestore/services/eventService');
	return getSpecificEventOf(animal, userID);
}

/**
 * Deletes an event and saves it to the log with a description
 * @deprecated Use eventService.deleteEventAndSaveToLog instead
 * @param {string} animal - MAC address of the animal's device
 * @param {Object} event - Event data
 * @param {string} userID - The unique identifier for the user
 * @param {string} desc - Description for the log (inconspicuous, conspicuous)
 * @param {string|null} [category=null] - Optional category for the event
 * @param {number|null} [timestamp=null] - Optional custom timestamp for the event
 * @returns {Promise<void>}
 * @path Users/{userID}/Events/{animal}
 * @path Users/{userID}/Animals/{animal}
 * @path Users/{userID}/Log
 */
export async function deleteEventAndSaveToLog(
	animal,
	event,
	userID,
	desc,
	category = null,
	timestamp = null
) {
	console.warn('[firestore][deleteEventAndSaveToLog] - DEPRECATED: Use eventService.deleteEventAndSaveToLog instead');
	const { deleteEventAndSaveToLog } = await import('../api/firestore/services/eventService');
	return deleteEventAndSaveToLog(animal, event, userID, desc, category, timestamp);
}

/**
 * Deletes all events and saves them to log
 * @deprecated Use eventService.deleteAllEventAndSaveToLog instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<void>}
 * @path Users/{userID}/Events
 * @path Users/{userID}/Log
 */
export async function deleteAllEventAndSaveToLog(userID) {
	console.warn('[firestore][deleteAllEventAndSaveToLog] - DEPRECATED: Use eventService.deleteAllEventAndSaveToLog instead');
	const { deleteAllEventAndSaveToLog } = await import('../api/firestore/services/eventService');
	return deleteAllEventAndSaveToLog(userID);
}

/**
 * Deletes all predictions and saves them to log
 * @deprecated Use predictionService.deleteAllPredictionsAndSaveToLog instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<void>}
 * @path Users/{userID}/Predictions
 * @path Users/{userID}/Log
 */
export async function deleteAllPredictionsAndSaveToLog(userID) {
	console.warn('[firestore][deleteAllPredictionsAndSaveToLog] - DEPRECATED: Use predictionService.deleteAllPredictionsAndSaveToLog instead');
	const { deleteAllPredictionsAndSaveToLog } = await import('../api/firestore/services/predictionService');
	return deleteAllPredictionsAndSaveToLog(userID);
}

/**
 * Gets all nodes for a user
 * @deprecated Use sensorService.getAllNodes instead
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of node objects
 * @path Users/{userID}/Nodes
 */
export async function getAllNodes(userID) {
	console.warn('[firestore][getAllNodes] - DEPRECATED: Use sensorService.getAllNodes instead');
	const { getAllNodes } = await import('../api/firestore/services/sensorService');
	return getAllNodes(userID);
}

/**
 * Gets a BioNode from the global BioNodes collection by ID
 * @deprecated Use sensorService.getBioNodeById instead
 * @param {string} nodeId - The BioNode ID (e.g., "2025.0001")
 * @returns {Promise<Object|null>} BioNode data with statistics, or null if not found
 * @path BioNodes/{nodeId}
 */
export async function getBioNodeById(nodeId) {
	console.warn('[firestore][getBioNodeById] - DEPRECATED: Use sensorService.getBioNodeById instead');
	const { getBioNodeById } = await import('../api/firestore/services/sensorService');
	return getBioNodeById(nodeId);
}

/**
 * Calculates the connectivity status of a node based on its last update timestamp
 * @deprecated Use sensorService.getNodeConnectivityStatus instead
 * @param {Object} node - Node object with timestamp field (t)
 * @param {number} currentTime - Current timestamp in milliseconds (optional, defaults to now)
 * @returns {Object} Status object with { status: 'healthy'|'warning'|'critical', timeDiff: number, message: string }
 */
export function getNodeConnectivityStatus(node, currentTime = null) {
	console.warn('[firestore][getNodeConnectivityStatus] - DEPRECATED: Use sensorService.getNodeConnectivityStatus instead');
	const { getNodeConnectivityStatus } = require('../api/firestore/services/sensorService');
	return getNodeConnectivityStatus(node, currentTime);
}

/**
 * Changes the group of a node
 * @deprecated Use sensorService.changeGroupOfNodes instead
 * @param {string} userID - The unique identifier for the user
 * @param {string} node - Node ID
 * @param {string} group - New group to assign
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Nodes/{node}
 */
export async function changeGroupOfNodes(userID, node, group) {
	console.warn('[firestore][changeGroupOfNodes] - DEPRECATED: Use sensorService.changeGroupOfNodes instead');
	const { changeGroupOfNodes } = await import('../api/firestore/services/sensorService');
	return changeGroupOfNodes(userID, node, group);
}

/**
 * Gets a specific animal by MAC address
 * @deprecated Use animalService.getAnimal instead
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @returns {Promise<Object>} Animal data
 * @path Users/{userID}/Animals/{mac}
 */
export async function getAnimal(userID, mac) {
	console.warn('[firestore][getAnimal] - DEPRECATED: Use animalService.getAnimal instead');
	const { getAnimal } = await import('../api/firestore/services/animalService');
	return getAnimal(userID, mac);
}

/**
 * Gets sensor data for an animal
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @param {string} [resolution="Day"] - Resolution of the data
 * @param {number} [startTime=null] - Starting timestamp to filter data (in milliseconds)
 * @returns {Promise<Array<Object>>} Array of sensor data points
 * @path Users/{userID}/Animals/{mac}/Sensordata/{resolution}
 */
export async function getSensordata(
	userID,
	mac,
	resolution = "Day",
	startTime = null
) {
	try {
		// Define the resampling interval (1 hour in milliseconds)
		const interval = 60 * 60 * 1000;

		// Reference to the Firestore document based on resolution
		const docRef = db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(mac)
			.collection("Sensordata")
			.doc(resolution);

		// Fetch the document data
		const doc = await docRef.get();

		if (!doc.exists) {
			console.warn(
				`No sensordata found for userID: ${userID}, mac: ${mac}, resolution: ${resolution}`
			);
			return [];
		}

		// Extract and validate sensordata
		const fetchedData = doc.data().Sensordata || [];

		if (!Array.isArray(fetchedData) || fetchedData.length === 0) {
			console.warn(
				`Sensordata is empty or invalid for userID: ${userID}, mac: ${mac}, resolution: ${resolution}`
			);
			return [];
		}

		// Sort data by timestamp (ascending)
		// Assuming 't' is a Unix timestamp in milliseconds
		const sortedData = fetchedData.sort((a, b) => a.t - b.t);

		// Filter data by timestamp if startTime is provided
		let filteredData = sortedData;
		if (startTime) {
			filteredData = sortedData.filter((item) => item.t >= startTime);

			// If no data after filtering, fall back to showing the most recent available data
			if (filteredData.length === 0 && sortedData.length > 0) {
				console.warn(
					`No data found after filtering by timestamp (startTime: ${startTime}), showing most recent available data`
				);
				// Return all available data so user can see historical information
				filteredData = sortedData;
			}
		}

		// If no data, return empty array
		if (filteredData.length === 0) {
			return [];
		}

		// Determine the range of the data for resampling
		const minTime = Math.floor(filteredData[0].t / interval) * interval; // Round down to the nearest hour
		const maxTime =
			Math.ceil(filteredData[filteredData.length - 1].t / interval) * interval; // Round up to the nearest hour

		const resampledData = [];
		let currentIntervalStart = minTime;
		let currentIntervalEnd = minTime + interval;
		let dataIndex = 0;

		const dataLength = filteredData.length;
		let previousValue = null;

		// Iterate over each hourly interval
		while (currentIntervalStart < maxTime) {
			const intervalData = [];

			// Collect all data points within the current interval
			while (
				dataIndex < dataLength &&
				filteredData[dataIndex].t < currentIntervalEnd
			) {
				intervalData.push(filteredData[dataIndex]);
				dataIndex++;
			}

			if (intervalData.length > 0) {
				// Calculate mean values for numeric fields using a single reduce pass
				const sum = intervalData.reduce(
					(acc, curr) => {
						acc.a += curr.a || 0;
						acc.aChange += curr.aChange || 0;
						acc.aNeighbours += curr.aNeighbours || 0;
						acc.c += curr.c || 0;
						acc.cChange += curr.cChange || 0;
						acc.cNeighbours += curr.cNeighbours || 0;
						acc.s += curr.s || 0;
						acc.count += 1;
						return acc;
					},
					{
						a: 0,
						aChange: 0,
						aNeighbours: 0,
						c: 0,
						cChange: 0,
						cNeighbours: 0,
						s: 0,
						count: 0,
					}
				);

				const count = sum.count;

				// Avoid division by zero
				if (count > 0) {
					const meanValue = {
						t: currentIntervalStart, // Timestamp for the interval start
						a: sum.a / count,
						aChange: sum.aChange / count,
						aNeighbours: sum.aNeighbours / count,
						c: sum.c / count,
						cChange: sum.cChange / count,
						cNeighbours: sum.cNeighbours / count,
						s: sum.s / count,
					};

					resampledData.push(meanValue);
					previousValue = meanValue;
				}
			} else if (previousValue) {
				// Find the next data point for interpolation
				let nextDataIndex = dataIndex;
				while (
					nextDataIndex < dataLength &&
					filteredData[nextDataIndex].t < currentIntervalEnd
				) {
					nextDataIndex++;
				}

				const nextValue = filteredData[nextDataIndex];

				if (nextValue) {
					const timeDiff = nextValue.t - previousValue.t;
					const factor = (currentIntervalStart - previousValue.t) / timeDiff;

					// Clamp factor between 0 and 1
					const clampedFactor = Math.min(Math.max(factor, 0), 1);

					const interpolatedValue = {
						t: currentIntervalStart,
						a:
							previousValue.a + clampedFactor * (nextValue.a - previousValue.a),
						aChange:
							previousValue.aChange +
							clampedFactor * (nextValue.aChange - previousValue.aChange),
						aNeighbours:
							previousValue.aNeighbours +
							clampedFactor *
								(nextValue.aNeighbours - previousValue.aNeighbours),
						c:
							previousValue.c + clampedFactor * (nextValue.c - previousValue.c),
						cChange:
							previousValue.cChange +
							clampedFactor * (nextValue.cChange - previousValue.cChange),
						cNeighbours:
							previousValue.cNeighbours +
							clampedFactor *
								(nextValue.cNeighbours - previousValue.cNeighbours),
						s:
							previousValue.s + clampedFactor * (nextValue.s - previousValue.s),
					};

					resampledData.push(interpolatedValue);
				}
			}

			// Move to the next interval
			currentIntervalStart += interval;
			currentIntervalEnd += interval;
		}

		return resampledData;
	} catch (error) {
		console.error("Error fetching sensordata:", error);
		throw error; // Propagate the error to be handled by the caller
	}
}

/**
 * Gets notes for an animal
 * @deprecated Use eventService.getNotes instead
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @returns {Promise<Array<Object>>} Array of note objects
 * @path Users/{userID}/GeneralEvents
 */
export async function getNotes(userID, mac) {
	console.warn('[firestore][getNotes] - DEPRECATED: Use eventService.getNotes instead');
	const { getNotes } = await import('../api/firestore/services/eventService');
	return getNotes(userID, mac);
}


/**
 * Gets general events for an animal (matches mobile app structure)
 * @param {string} userID - The unique identifier for the user
 * @param {string} mac - MAC address of the animal's device
 * @returns {Promise<Array<Object>>} Array of general event objects sorted by timestamp descending
 * @path Users/{userID}/GeneralEvents
 * Structure: {Archived, MAC, Uid, t, ID, Diagnose, Description, Token?}
 */
export async function getGeneralEvents(userID, mac) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("GeneralEvents")
			.where("MAC", "==", mac)
			.get();

		const events = [];
		snapshot.forEach((doc) => {
			const data = doc.data();
			// Ensure proper structure with defaults matching mobile app
			events.push({
				Archived: data.Archived || false,
				MAC: data.MAC || mac,
				Uid: data.Uid || data.MAC || mac,
				t: data.t || parseInt(doc.id) || Date.now(),
				ID: data.ID || "",
				Diagnose: data.Diagnose || "",
				Description: data.Description || "",
				Token: data.Token,
			});
		});

		// Sort by timestamp descending (newest first) as per mobile app
		events.sort((a, b) => b.t - a.t);

		return events;
	} catch (error) {
		console.error("Error fetching general events:", error);
		throw error;
	}
}

/**
 * Fetches all general events (notes) for a user across all animals
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<Array>} Array of general event records
 * @path Users/{userID}/GeneralEvents
 */
export async function getAllGeneralEvents(userID) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userID)
			.collection("GeneralEvents")
			.where("Archived", "==", false)
			.get();

		const events = [];
		snapshot.forEach((doc) => {
			const data = doc.data();
			// Ensure proper structure with defaults matching mobile app
			events.push({
				Archived: data.Archived || false,
				MAC: data.MAC || "",
				Uid: data.Uid || data.MAC || "",
				t: data.t || parseInt(doc.id) || Date.now(),
				ID: data.ID || "",
				Diagnose: data.Diagnose || "",
				Description: data.Description || "",
				Token: data.Token,
			});
		});

		// Sort by timestamp descending (newest first)
		events.sort((a, b) => b.t - a.t);

		return events;
	} catch (error) {
		console.error("Error fetching all general events:", error);
		throw error;
	}
}

/**
 * Gets billing state for a user
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<{BillingState: string, BillingDate: number|null, BillingDateEnd: number|null, BillingDateStart: number|null, BillingPrice: number, ActiveTags: number, FarmSize?: number, ActiveAnimals?: number, TrialStartDate?: number, TrialEndDate?: number, IsTrialActive?: boolean, PackageTier?: string, SubscriptionStatus?: string}>} Billing state
 * @path Users/{userID}/Account/Billing
 */
export async function getUserBillingState(userID) {
	let state = {
		BillingState: "Free",
		BillingDate: null,
		BillingDateEnd: null,
		BillingDateStart: null,
		BillingPrice: 0,
		ActiveTags: 0,
		FarmSize: 0,
		ActiveAnimals: 0,
		TrialStartDate: null,
		TrialEndDate: null,
		IsTrialActive: false,
		PackageTier: null,
		SubscriptionStatus: null,
	};

	await db
		.collection("Users")
		.doc(userID)
		.collection("Account")
		.doc("Billing")
		.get()
		.then(async (doc) => {
			// @ts-ignore
			if (doc.exists) {
				state = doc.data();
				
				// Ensure backwards compatibility: map ActiveTags to FarmSize if needed
				if (state.ActiveTags && !state.FarmSize) {
					state.FarmSize = state.ActiveTags;
				}
				if (state.FarmSize && !state.ActiveAnimals) {
					state.ActiveAnimals = state.FarmSize;
				}
			} else {
				// If no billing record exists, try to initialize trial
				try {
					const { initializeFreeTrial } = await import("../api/firestore/services/billingService");
					await initializeFreeTrial(userID);
					// Re-fetch after initialization
					const updatedDoc = await db
						.collection("Users")
						.doc(userID)
						.collection("Account")
						.doc("Billing")
						.get();
					if (updatedDoc.exists) {
						state = updatedDoc.data();
					}
				} catch (error) {
					console.error("[firestore][getUserBillingState] - Error initializing trial:", error);
					// Continue with default state
				}
			}
		})
		.catch((e) => {
			throw e;
		});

	return state;
}

/**
 * Sends a FCM message to a specific token
 * @param {string} userId - The unique identifier for the user
 * @param {string} token - The FCM token to send the message to
 * @param {Object} message - The message to send
 * @returns {Promise<void>}
 * @path Users/{userId}/Account/Messages
 */
export async function sendFCMMessageToToken(userId, token, message) {
	await db
		.collection("Users")
		.doc(userId)
		.collection("Account")
		.doc("Messages")
		.set(
			{
				Token: token,
				Message: message,
				t: new Date().getTime(),
			},
			{ merge: true }
		)
		.catch((e) => {
			throw e;
		});
}

/**
 * Gets barn settings
 * @param {string} userId - The unique identifier for the user
 * @param {string} name - The name of the barn
 * @returns {Promise<Object>} Barn settings
 * @path Users/{userId}/Barn/{name}
 */
export async function getBarnSettings(userId, name) {
	let barn;

	await db
		.collection("Users")
		.doc(userId)
		.collection("Barn")
		.doc(name)
		.get()
		.then((doc) => {
			barn = doc.data();
		})
                .catch((e) => {
                        throw e;
                });

	return barn;
}

/**
 * Gets barn layout for a specific barn
 * @param {string} userID - The unique identifier for the user
 * @param {string} barnName - The name of the barn
 * @returns {Promise<Object|null>} Barn layout object or null if not found
 * @path Users/{userID}/Barn/{barnName}/Layout
 */
export async function getBarnLayout(userID, barnName) {
	console.warn("[firestore][getBarnLayout] - DEPRECATED: Use barnService.getBarnLayout instead");
	const { getBarnLayout } = await import("../api/firestore/services/barnService");
	return getBarnLayout(userID, barnName);
}

/**
 * Saves or updates barn layout
 * @param {string} userID - The unique identifier for the user
 * @param {string} barnName - The name of the barn
 * @param {Object} layout - Layout object with pens array
 * @returns {Promise<void>}
 * @path Users/{userID}/Barn/{barnName}/Layout
 */
export async function saveBarnLayout(userID, barnName, layout) {
	console.warn("[firestore][saveBarnLayout] - DEPRECATED: Use barnService.saveBarnLayout instead");
	const { saveBarnLayout } = await import("../api/firestore/services/barnService");
	return saveBarnLayout(userID, barnName, layout);
}

/**
 * Assigns an animal to a pen in the barn layout
 * @param {string} userID - The unique identifier for the user
 * @param {string} barnName - The name of the barn
 * @param {string} penId - The ID of the pen
 * @param {string} animalMAC - The MAC address of the animal to assign
 * @returns {Promise<void>}
 * @path Users/{userID}/Barn/{barnName}/Layout
 */
export async function assignAnimalToPen(userID, barnName, penId, animalMAC) {
	console.warn("[firestore][assignAnimalToPen] - DEPRECATED: Use barnService.assignAnimalToPen instead");
	const { assignAnimalToPen } = await import("../api/firestore/services/barnService");
	return assignAnimalToPen(userID, barnName, penId, animalMAC);
}

/**
 * Removes an animal from a pen in the barn layout
 * @param {string} userID - The unique identifier for the user
 * @param {string} barnName - The name of the barn
 * @param {string} penId - The ID of the pen
 * @param {string} animalMAC - The MAC address of the animal to remove
 * @returns {Promise<void>}
 * @path Users/{userID}/Barn/{barnName}/Layout
 */
export async function removeAnimalFromPen(userID, barnName, penId, animalMAC) {
	console.warn("[firestore][removeAnimalFromPen] - DEPRECATED: Use barnService.removeAnimalFromPen instead");
	const { removeAnimalFromPen } = await import("../api/firestore/services/barnService");
	return removeAnimalFromPen(userID, barnName, penId, animalMAC);
}

/**
 * Gets all ants for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of ant objects with activity status
 * @path Users/{userId}/Ants
 */
export async function getAnts(userId) {
	let ants = [];

	await db
		.collection("Users")
		.doc(userId)
		.collection("Ants")
		.get()
		.then((snapshot) => {
			snapshot.forEach((doc) => {
				let document = doc.data();
				document.isActive =
					new Date().getTime() - document.t < 1000 * 60 * 60 * 24;
				ants.push(document);
			});
		})
                .catch((e) => {
                        throw e;
                });

	return ants;
}

/**
 * Gets all treatments for a user
 * @deprecated Delegates to api/firestore/services/treatmentService — import from there.
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of treatment objects
 * @path Users/{userId}/Treatments
 */
export async function getTreatments(userId, options = {}) {
        return treatmentService.getTreatments(userId, options);
}

/**
 * Gets node positions
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of node position objects
 * @path Users/{userId}/RoomPositions
 */
export async function getNodePositions(userId) {
	console.warn("[firestore][getNodePositions] - DEPRECATED: Use barnService.getNodePositions instead");
	const { getNodePositions } = await import("../api/firestore/services/barnService");
	return getNodePositions(userId);
}

/**
 * Sets node position
 * @param {string} userId - The unique identifier for the user
 * @param {string} node - Node ID
 * @param {Object} position - Position data
 * @returns {Promise<void>}
 * @path Users/{userId}/RoomPositions/{node}
 */
export async function setNodePosition(userId, node, position) {
	console.warn("[firestore][setNodePosition] - DEPRECATED: Use barnService.setNodePosition instead");
	const { setNodePosition } = await import("../api/firestore/services/barnService");
	return setNodePosition(userId, node, position);
}

/**
 * Edits a treatment
 * @param {string} userID - The unique identifier for the user
 * @param {Object} document - Treatment data
 * @param {number} document.t - Timestamp used as document ID
 * @returns {Promise<void>}
 * @path Users/{userID}/Treatments/{document.t}
 */
export async function editTreatment(userID, document) {
        return treatmentService.editTreatment(userID, document);
}

/**
 * Deletes a treatment
 * @param {string} userID - The unique identifier for the user
 * @param {number|string} t - Timestamp used as document ID
 * @returns {Promise<void>}
 * @path Users/{userID}/Treatments/{t}
 */
export async function deleteTreatment(userID, t) {
        return treatmentService.deleteTreatment(userID, t);
}

/**
 * Creates a new treatment
 * @param {string} userID - The unique identifier for the user
 * @param {Object} treatment - Treatment data
 * @param {boolean} treatment.Archived - Whether the treatment is archived
 * @param {string} treatment.ID - Animal ID
 * @param {number} treatment.t - Timestamp used as document ID
 * @param {string} treatment.Diagnose - Diagnosis
 * @param {string} treatment.Medicine - Medicine name
 * @param {number|string} treatment.TreatmentAmount - Treatment amount
 * @param {number|string} treatment.TreatmentCount - Treatment count
 * @param {string} treatment.TreatmentInterval - Treatment interval
 * @param {string} treatment.Comment - Additional comments
 * @returns {Promise<boolean>} Success status
 * @path Users/{userID}/Treatments/{treatment.t}
 */
export async function setNewTreatment(userID, treatment) {
        return treatmentService.setNewTreatment(userID, treatment);
}

/**
 * Deletes a work task from the user's WorkHistory collection
 * @param {string} userId - The unique identifier for the user
 * @param {string} taskId - The task ID to delete
 * @returns {Promise<void>}
 * @path Users/{userId}/WorkHistory/{taskId}
 */
export async function deleteWorkTask(userId, taskId) {
        try {
                await db
                        .collection("Users")
                        .doc(userId)
                        .collection("WorkHistory")
                        .doc(taskId)
                        .delete();

                invalidateCache(`${CACHE_NAMESPACES.WORK_ITEMS}:${userId}`);
        } catch (error) {
                console.error("Error deleting work task:", error);
                throw error;
        }
}

/**
 * Updates the selected billing tier for a user.
 * @param {string} userID - The unique identifier for the user
 * @param {string} selectedTier - Billing tier id to store
 * @returns {Promise<void>}
 * @path Users/{userID}
 */
export async function updateSelectedBillingTier(userID, selectedTier) {
        if (!userID) {
                throw new Error("Missing user identifier");
        }

        const allowedTiers = ["free", "small", "medium", "large", "enterprise"];
        const tierToStore = allowedTiers.includes(selectedTier) ? selectedTier : "free";

        try {
                await db
                        .collection("Users")
                        .doc(userID)
                        .set(
                                {
                                        SelectedBillingTier: tierToStore,
                                        UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                                },
                                { merge: true }
                        );
        } catch (error) {
                console.error("[firestore][updateSelectedBillingTier] - Error:", error);
                throw error;
        }
}

/**
 * Ensures a user document exists with baseline defaults
 * @param {string} userID - The unique identifier for the user
 * @param {string} email - User email address
 * @param {boolean} [initializeTrial=false] - Whether to initialize trial (only if email verified)
 * @param {string} [selectedTier=null] - Selected billing tier from signup
 * @returns {Promise<void>}
 * @path Users/{userID}
 */
export async function ensureUserDocument(userID, email = "", initializeTrial = false, selectedTier = null) {
        try {
                const userRef = db.collection("Users").doc(userID);
                const snapshot = await userRef.get();

                const baseData = {
                        Email: email || "",
                        Flagged: false,
                        Inactive: false,
                        Uid: userID,
                };

                if (!snapshot.exists) {
                        const userData = {
                                ...baseData,
                                Devices: [],
                                ProfileComplete: false,
                                noEartags: true, // New SaaS users have no eartags by default
                                CreatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                                UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                        };
                        
                        // Store selected tier if provided (from signup)
                        if (selectedTier) {
                                userData.SelectedBillingTier = selectedTier;
                        }
                        
                        await userRef.set(userData, { merge: true });
                        
                        // Initialize free trial only if requested and email is verified
                        if (initializeTrial) {
                                try {
                                        // Check if email is verified before initializing trial
                                        // We'll initialize the trial - the billing service will check verification
                                        // when actually granting access, but we can initialize the trial period here
                                        const { initializeFreeTrial } = await import("../api/firestore/services/billingService");
                                        
                                        // Note: initializeFreeTrial will create the billing record
                                        // The actual access will be validated by validateBillingAccess which checks email verification
                                        // Pass selected tier if available
                                        await initializeFreeTrial(userID, selectedTier);
                                } catch (error) {
                                        console.error("[firestore][ensureUserDocument] - Error initializing trial:", error);
                                        // Don't fail user creation if trial initialization fails
                                }
                        }
                } else {
                        const data = snapshot.data() || {};
                        const payload = {
                                ...baseData,
                                UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                        };

                        if (!Array.isArray(data.Devices)) {
                                payload.Devices = [];
                        }

                        if (data.ProfileComplete === undefined) {
                                payload.ProfileComplete = false;
                        }

                        await userRef.set(payload, { merge: true });
                }
        } catch (error) {
                console.error("Error ensuring user document:", error);
                throw error;
        }
}

/**
 * Registers or updates the web device profile for the user
 * @param {string} userID - The unique identifier for the user
 * @param {{deviceId: string, name?: string, userAgent?: string, email?: string}} deviceInfo - Device metadata
 * @returns {Promise<{deviceRecord: Object, profileComplete: boolean}>}
 * @path Users/{userID}
 */
export async function upsertUserDevice(userID, deviceInfo) {
        if (!userID) {
                throw new Error("Missing user identifier");
        }

        const { deviceId, name, userAgent, email } = deviceInfo;

        if (!deviceId) {
                throw new Error("Missing device identifier");
        }

        await ensureUserDocument(userID, email);

        const userRef = db.collection("Users").doc(userID);
        const snapshot = await userRef.get();
        const data = snapshot.data() || {};
        const devices = Array.isArray(data.Devices) ? [...data.Devices] : [];
        const now = Date.now();

        const deviceIndex = devices.findIndex((device) => device.DeviceId === deviceId);
        let deviceRecord;

        if (deviceIndex > -1) {
                deviceRecord = {
                        ...devices[deviceIndex],
                        DeviceId: deviceId,
                        Name: name || devices[deviceIndex].Name || "Web Device",
                        UserAgent: userAgent || devices[deviceIndex].UserAgent || "",
                        LastLogin: now,
                };
                devices[deviceIndex] = deviceRecord;
        } else {
                deviceRecord = {
                        DeviceId: deviceId,
                        Name: name || "Web Device",
                        CustomName: "",
                        Rank: 3,
                        LastLogin: now,
                        UserAgent: userAgent || "",
                };
                devices.push(deviceRecord);
        }

        const profileComplete = devices.some(
                (device) => typeof device.CustomName === "string" && device.CustomName.trim() !== ""
        );

        await userRef.set(
                {
                        Email: data.Email || email || "",
                        Devices: devices,
                        ProfileComplete: profileComplete,
                        UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                },
                { merge: true }
        );

        return { deviceRecord, profileComplete };
}

/**
 * Completes the device profile with a custom name and rank
 * @param {string} userID - The unique identifier for the user
 * @param {string} deviceId - The device identifier stored locally
 * @param {{ customName: string, rank: number }} updates - Profile updates
 * @returns {Promise<{deviceRecord: Object, profileComplete: boolean}>}
 * @path Users/{userID}
 */
export async function updateUserDeviceProfile(userID, deviceId, updates) {
        if (!userID) {
                throw new Error("Missing user identifier");
        }

        if (!deviceId) {
                throw new Error("Missing device identifier");
        }

        const trimmedName = (updates.customName || "").trim();

        if (!trimmedName) {
                throw new Error("custom-name-required");
        }

        const normalizedRank = [1, 2, 3].includes(Number(updates.rank))
                ? Number(updates.rank)
                : 3;

        const userRef = db.collection("Users").doc(userID);

        const result = await db.runTransaction(async (transaction) => {
                const snapshot = await transaction.get(userRef);

                if (!snapshot.exists) {
                        throw new Error("User document not found");
                }

                const data = snapshot.data() || {};
                const devices = Array.isArray(data.Devices) ? [...data.Devices] : [];
                const deviceIndex = devices.findIndex((device) => device.DeviceId === deviceId);

                if (deviceIndex === -1) {
                        throw new Error("device-not-registered");
                }

                const duplicate = devices.find(
                        (device, index) =>
                                index !== deviceIndex &&
                                typeof device.CustomName === "string" &&
                                device.CustomName.trim().toLowerCase() === trimmedName.toLowerCase()
                );

                if (duplicate) {
                        throw new Error("custom-name-already-exists");
                }

                const updatedDevice = {
                        ...devices[deviceIndex],
                        CustomName: trimmedName,
                        Rank: normalizedRank,
                        LastLogin: Date.now(),
                };

                devices[deviceIndex] = updatedDevice;

                const profileComplete = devices.some(
                        (device) => typeof device.CustomName === "string" && device.CustomName.trim() !== ""
                );

                transaction.set(
                        userRef,
                        {
                                Devices: devices,
                                ProfileComplete: profileComplete,
                                UpdatedAt: firebase.firestore.FieldValue.serverTimestamp(),
                        },
                        { merge: true }
                );

                return { deviceRecord: updatedDevice, profileComplete };
        });

        return result;
}

/**
 * Creates or updates a user document with provided data
 * @param {string} userID - The unique identifier for the user
 * @param {Object} userData - User data object containing profile information
 * @returns {Promise<void>}
 * @path Users/{userID}
 */
export async function createUserDocument(userID, userData) {
	try {
		// Convert the userData to match the expected format
                const formattedUserData = {
                        Email: userData.email || "",
                        Name: userData.displayName || "",
                        Phone: userData.phone || "",
                        Language: userData.language || "en",
                        WedaIntegration:
                                userData.wedaIntegration !== undefined
                                        ? userData.wedaIntegration
                                        : false,
                        IsResearch:
                                userData.isResearch !== undefined ? userData.isResearch : false,
                        Type: userData.type || "pig",
                        Uid: userID,
                        // Store role information in a way that won't conflict with existing structure
                        Role: userData.role || "user",
                };

                if (userData.profileComplete !== undefined) {
                        formattedUserData.ProfileComplete = Boolean(userData.profileComplete);
                }

                if (Array.isArray(userData.devices)) {
                        formattedUserData.Devices = userData.devices;
                }

                if (userData.flagged !== undefined) {
                        formattedUserData.Flagged = Boolean(userData.flagged);
                }

                if (userData.inactive !== undefined) {
                        formattedUserData.Inactive = Boolean(userData.inactive);
                }

                // Set the document with merge option to update fields without overwriting the entire document
                await db
                        .collection("Users")
                        .doc(userID)
			.set(formattedUserData, { merge: true });

		// Don't return anything (void function)
	} catch (e) {
		console.error("Error creating user document:", e);
		throw e;
	}
}

/**
 * Marks an animal as healed by removing its diagnosis status
 * @param {string} userID - The unique identifier for the user
 * @param {string} animal - MAC address of the animal's device
 * @returns {Promise<void>}
 * @path Users/{userID}/Animals/{animal}
 */
export async function markAnimalAsHealed(userID, animal) {
	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("Animals")
			.doc(animal)
			.update({
				Diagnosed: null,
			});

		// Create a recovery event in GeneralEvents collection
		const recoveryEvent = {
			ID: animal,
			Diagnose: "Recovered",
			Archived: false,
			Description: "Animal marked as recovered",
			MAC: animal,
			Uid: userID,
			t: new Date().getTime(),
		};

		await db
			.collection("Users")
			.doc(userID)
			.collection("GeneralEvents")
			.doc(recoveryEvent.t.toString())
			.set(recoveryEvent);
	} catch (error) {
		console.error("Error marking animal as healed:", error);
		throw error;
	}
}

/**
 * Creates or updates a note for an animal
 * @param {string} userID - The unique identifier for the user
 * @param {string} animalMAC - MAC address of the animal's device
 * @param {string} description - Note description/content
 * @param {number} timestamp - Timestamp for the note (or null for current time)
 * @param {string} [noteID=null] - Existing note ID for updates, or null for new notes
 * @param {string} [diagnose="Note"] - Type/category of the note
 * @returns {Promise<string>} The ID of the created/updated note
 * @path Users/{userID}/GeneralEvents/{noteID or generated timestamp}
 */
export async function createOrUpdateNote(
	userID,
	animalMAC,
	description,
	timestamp = null,
	noteID = null,
	diagnose = "Note"
) {
	// Use provided timestamp or generate a new one
	const noteTime = timestamp || new Date().getTime();

	// Use provided noteID or generate one from the timestamp
	const docID = noteID || noteTime.toString();

	const noteData = {
		ID: animalMAC,
		Diagnose: diagnose,
		Archived: false,
		Description: description,
		MAC: animalMAC,
		Uid: userID,
		t: noteTime,
	};

	try {
		await db
			.collection("Users")
			.doc(userID)
			.collection("GeneralEvents")
			.doc(docID)
			.set(noteData);

		return docID;
	} catch (e) {
		console.error("Error creating/updating note:", e);
		throw e;
	}
}

/**
 * Gets diagnose categories for treatments.
 * @deprecated Delegates to api/firestore/services/treatmentService — import from there.
 */
export const getDiagnoseCategories = async (userId) => {
	return treatmentService.getDiagnoseCategories(userId);
};

/**
 * Gets medicines list for treatments.
 * @deprecated Delegates to api/firestore/services/treatmentService — import from there.
 */
export const getMedicines = async (userId) => {
	return treatmentService.getMedicines(userId);
};

/**
 * Gets custom prediction thresholds for a user (Heat, Birth, Lameness)
 * @param {string} userID - The unique identifier for the user
 * @returns {Promise<{Heat: number, Birth: number, Lameness: number}>} Prediction threshold settings (values between 60-95)
 * @path Users/{userID}/Account/PredictionThresholds
 */
export async function getCustomPredictionThresholds(userID) {
	let thresholds = {
		Heat: 75, // Default 75%
		Birth: 75, // Default 75%
		Lameness: 70, // Default 70%
	};

	try {
		const doc = await db
			.collection("Users")
			.doc(userID)
			.collection("Account")
			.doc("PredictionThresholds")
			.get();

		if (doc.exists && doc.data()) {
			const data = doc.data();
			// Ensure values are within valid range (60-95)
			thresholds.Heat = Math.min(95, Math.max(60, data.Heat || 75));
			thresholds.Birth = Math.min(95, Math.max(60, data.Birth || 75));
			thresholds.Lameness = Math.min(95, Math.max(60, data.Lameness || 70));
		}
	} catch (e) {
		console.error("Error getting prediction thresholds:", e);
		// Return defaults on error
	}

	return thresholds;
}

/**
 * Sets custom prediction thresholds for a user
 * @param {string} userID - The unique identifier for the user
 * @param {Object} thresholds - Prediction threshold settings to save
 * @param {number} thresholds.Heat - Heat threshold (60-95)
 * @param {number} thresholds.Birth - Birth threshold (60-95)
 * @param {number} thresholds.Lameness - Lameness threshold (60-95)
 * @returns {Promise<void>}
 * @path Users/{userID}/Account/PredictionThresholds
 */
export async function setCustomPredictionThresholds(userID, thresholds) {
	// Validate thresholds are within range
	const validatedThresholds = {
		Heat: Math.min(95, Math.max(60, thresholds.Heat || 75)),
		Birth: Math.min(95, Math.max(60, thresholds.Birth || 75)),
		Lameness: Math.min(95, Math.max(60, thresholds.Lameness || 70)),
	};

	await db
		.collection("Users")
		.doc(userID)
		.collection("Account")
		.doc("PredictionThresholds")
		.set(validatedThresholds, { merge: true })
		.catch((e) => {
			throw e;
		});
}

/**
 * Writes a new prediction validation
 * @param {string} userId - The unique identifier for the user
 * @param {Object} validationData - The validation data to store
 * @param {string} validationData.animalId - Animal ID
 * @param {string} validationData.animalMAC - Animal MAC address
 * @param {string} validationData.animalName - Animal name
 * @param {string} validationData.predictionType - Type of prediction (heat, birth, lameness)
 * @param {boolean} validationData.isCorrect - Whether the prediction was correct
 * @param {number} validationData.detectionTimestamp - When the prediction was detected
 * @param {string} validationData.notes - Optional notes
 * @param {Object} validationData.detectionFields - Relevant flag/detection field values
 * @returns {Promise<string>} The ID of the created validation document
 * @path Users/{userId}/PredictionValidations/{validationId}
 */
export async function writePredictionValidation(userId, validationData) {
	try {
		const validationId = `${validationData.animalMAC}_${validationData.predictionType}_${validationData.detectionTimestamp}`;
		
		// Helper function to recursively remove undefined values from an object
		const sanitizeObject = (obj) => {
			if (obj === null || obj === undefined) {
				return null;
			}
			if (Array.isArray(obj)) {
				return obj.map(sanitizeObject).filter(item => item !== undefined);
			}
			if (typeof obj === 'object') {
				const sanitized = {};
				for (const [key, value] of Object.entries(obj)) {
					if (value !== undefined) {
						sanitized[key] = sanitizeObject(value);
					}
				}
				return sanitized;
			}
			return obj;
		};

		// Sanitize the entire validation data to remove any undefined values
		const sanitizedValidationData = sanitizeObject(validationData);
		
		const validationDoc = {
			animalId: sanitizedValidationData.animalId,
			animalMAC: sanitizedValidationData.animalMAC,
			animalName: sanitizedValidationData.animalName,
			predictionType: sanitizedValidationData.predictionType,
			isCorrect: sanitizedValidationData.isCorrect,
			detectionTimestamp: sanitizedValidationData.detectionTimestamp,
			validatedAt: Date.now(),
			validatedBy: userId,
			notes: sanitizedValidationData.notes || "",
			detectionFields: sanitizedValidationData.detectionFields || {}
		};

		await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.doc(validationId)
			.set(validationDoc)
			.catch((e) => {
				throw e;
			});

		return validationId;
	} catch (error) {
		console.error("Error writing prediction validation:", error);
		throw error;
	}
}

/**
 * Gets all prediction validations for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array<Object>>} Array of validation objects
 * @path Users/{userId}/PredictionValidations
 */
export async function getPredictionValidations(userId) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.orderBy("validatedAt", "desc")
			.get()
			.catch((e) => {
				throw e;
			});

		const validations = [];
		snapshot.forEach((doc) => {
			validations.push({
				id: doc.id,
				...doc.data()
			});
		});

		return validations;
	} catch (error) {
		console.error("Error getting prediction validations:", error);
		throw error;
	}
}

/**
 * Updates an existing prediction validation
 * @param {string} userId - The unique identifier for the user
 * @param {string} validationId - The ID of the validation to update
 * @param {Object} updates - The updates to apply
 * @returns {Promise<void>}
 * @path Users/{userId}/PredictionValidations/{validationId}
 */
export async function updatePredictionValidation(userId, validationId, updates) {
	try {
		await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.doc(validationId)
			.update({
				...updates,
				updatedAt: Date.now()
			})
			.catch((e) => {
				throw e;
			});
	} catch (error) {
		console.error("Error updating prediction validation:", error);
		throw error;
	}
}

/**
 * Gets prediction validations for a specific animal and prediction type
 * @param {string} userId - The unique identifier for the user
 * @param {string} animalMAC - The animal's MAC address
 * @param {string} predictionType - The type of prediction
 * @returns {Promise<Array<Object>>} Array of validation objects
 * @path Users/{userId}/PredictionValidations
 */
export async function getPredictionValidationsForAnimal(userId, animalMAC, predictionType) {
	try {
		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionValidations")
			.where("animalMAC", "==", animalMAC)
			.where("predictionType", "==", predictionType)
			.orderBy("validatedAt", "desc")
			.get()
			.catch((e) => {
				throw e;
			});

		const validations = [];
		snapshot.forEach((doc) => {
			validations.push({
				id: doc.id,
				...doc.data()
			});
		});

		return validations;
	} catch (error) {
		console.error("Error getting prediction validations for animal:", error);
		throw error;
	}
}

/**
 * Uploads a feedback image to Firebase Storage
 * @param {string} userId - The unique identifier for the user
 * @param {File} file - The image file to upload
 * @returns {Promise<string>} The download URL of the uploaded image
 * @path Storage: Users/{userId}/Feedback/Images/{timestamp}_{filename}
 */
export async function uploadFeedbackImage(userId, file) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}

		if (!file) {
			throw new Error("File is required");
		}

		// Validate file type
		const validImageTypes = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];
		if (!validImageTypes.includes(file.type)) {
			throw new Error("Invalid file type. Only PNG, JPG, JPEG, GIF, and WEBP are allowed.");
		}

		// Validate file size (50MB = 50 * 1024 * 1024 bytes)
		const maxSizeBytes = 10 * 1024 * 1024;
		if (file.size > maxSizeBytes) {
			throw new Error("File size exceeds 10MB limit.");
		}

		// Create storage reference
		const storage = firebase.storage(app);
		const timestamp = Date.now();
		const sanitizedFilename = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
		// Change "Users" to "users" (lowercase) to match Firebase Storage rules
		const storagePath = `users/${userId}/Feedback/Images/${timestamp}_${sanitizedFilename}`;
		const storageRef = storage.ref(storagePath);

		// Upload file
		const snapshot = await storageRef.put(file);
		const downloadURL = await snapshot.ref.getDownloadURL();

		return downloadURL;
	} catch (error) {
		console.error("[firestore][uploadFeedbackImage] - Error uploading image:", error);
		throw error;
	}
}

/**
 * Saves user feedback to Firestore
 * @param {string} userId - The unique identifier for the user
 * @param {Object} feedbackData - The feedback data
 * @param {number} feedbackData.rating - Priority from 1 to 3 (1=Essential, 2=Important, 3=Nice-to-have)
 * @param {string|null} feedbackData.feedback - Optional feedback text
 * @param {string|null} feedbackData.imageUrl - Optional image download URL
 * @param {number} [feedbackData.feedbackVersion=2] - Schema version for backward compat
 * @returns {Promise<string>} The document ID of the saved feedback
 * @path Users/{userId}/Feedback/{feedbackId}
 */
export async function saveUserFeedback(userId, feedbackData) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}

		if (!feedbackData || typeof feedbackData.rating !== "number") {
			throw new Error("Priority is required and must be a number");
		}

		if (feedbackData.rating < 1 || feedbackData.rating > 3) {
			throw new Error("Priority must be between 1 and 3");
		}

		const feedbackRef = await db
			.collection("Users")
			.doc(userId)
			.collection("Feedback")
			.add({
				rating: feedbackData.rating,
				feedback: feedbackData.feedback || null,
				imageUrl: feedbackData.imageUrl || (feedbackData.imageUrls && feedbackData.imageUrls.length > 0 ? feedbackData.imageUrls[0] : null),
				imageUrls: feedbackData.imageUrls || (feedbackData.imageUrl ? [feedbackData.imageUrl] : []),
				userId: userId,
				status: "pending",
				createdAt: firebase.firestore.FieldValue.serverTimestamp(),
				timestamp: firebase.firestore.FieldValue.serverTimestamp(),
				updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
				feedbackVersion: feedbackData.feedbackVersion || 2,
				// AI conversation fields - initialized for AI enrichment flow
				aiConversation: feedbackData.aiConversation || {
					status: "pending",
					startedAt: null,
					completedAt: null,
					questionCount: 0,
					messages: []
				},
				aiEnrichment: null,
				enrichedFeedback: null,
				aiVersion: null
			})
			.catch((e) => {
				throw e;
			});

		return feedbackRef.id;
	} catch (error) {
		console.error("[firestore][saveUserFeedback] - Error saving feedback:", error);
		throw error;
	}
}

/**
 * Gets all feedback submissions for a user
 * @param {string} userId - The unique identifier for the user
 * @returns {Promise<Array>} Array of feedback objects with document IDs
 * @path Users/{userId}/Feedback/{feedbackId}
 */
export async function getUserFeedback(userId) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}

		const snapshot = await db
			.collection("Users")
			.doc(userId)
			.collection("Feedback")
			.orderBy("timestamp", "desc")
			.get();

		const feedback = [];
		snapshot.forEach((doc) => {
			feedback.push({
				id: doc.id,
				...doc.data(),
			});
		});

		return feedback;
	} catch (error) {
		console.error("[firestore][getUserFeedback] - Error fetching feedback:", error);
		throw error;
	}
}

/**
 * Updates the AI conversation status and messages for a feedback submission
 * @param {string} userId - The unique identifier for the user
 * @param {string} feedbackId - The feedback document ID
 * @param {Object} updates - The fields to update
 * @returns {Promise<boolean>} Success status
 * @path Users/{userId}/Feedback/{feedbackId}
 */
export async function updateFeedbackConversation(userId, feedbackId, updates) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}

		if (!feedbackId) {
			throw new Error("Feedback ID is required");
		}

		await db
			.collection("Users")
			.doc(userId)
			.collection("Feedback")
			.doc(feedbackId)
			.update({
				...updates,
				updatedAt: firebase.firestore.FieldValue.serverTimestamp()
			});

		return true;
	} catch (error) {
		console.error("[firestore][updateFeedbackConversation] - Error updating conversation:", error);
		throw error;
	}
}

/**
 * Updates the status of a feedback ticket
 * @param {string} userId - The unique identifier for the user
 * @param {string} feedbackId - The feedback document ID
 * @param {string} status - New status: "pending", "in_progress", or "resolved"
 * @param {string} [adminNotes] - Optional admin notes about the status change
 * @returns {Promise<boolean>} Success status
 * @path Users/{userId}/Feedback/{feedbackId}
 */
export async function updateFeedbackStatus(userId, feedbackId, status, adminNotes) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}
		if (!feedbackId) {
			throw new Error("Feedback ID is required");
		}

		const validStatuses = ["pending", "in_progress", "resolved"];
		if (!validStatuses.includes(status)) {
			throw new Error(`Invalid status: ${status}. Must be one of: ${validStatuses.join(", ")}`);
		}

		const updateData = {
			status,
			updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
		};

		if (adminNotes !== undefined) {
			updateData.adminNotes = adminNotes;
		}

		// Track status history
		updateData.statusHistory = firebase.firestore.FieldValue.arrayUnion({
			status,
			changedAt: new Date().toISOString(),
			adminNotes: adminNotes || null,
		});

		await db
			.collection("Users")
			.doc(userId)
			.collection("Feedback")
			.doc(feedbackId)
			.update(updateData);

		return true;
	} catch (error) {
		console.error("[firestore][updateFeedbackStatus] - Error updating feedback status:", error);
		throw error;
	}
}

/**
 * Adds a message to the feedback AI conversation
 * @param {string} userId - The unique identifier for the user
 * @param {string} feedbackId - The feedback document ID
 * @param {Object} message - The message to add
 * @param {string} message.role - 'ai' or 'user'
 * @param {string} message.content - Message content
 * @param {Array} [message.options] - Options for AI messages
 * @returns {Promise<boolean>} Success status
 * @path Users/{userId}/Feedback/{feedbackId}
 */
export async function addFeedbackMessage(userId, feedbackId, message) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}

		if (!feedbackId) {
			throw new Error("Feedback ID is required");
		}

		if (!message || !message.role || !message.content) {
			throw new Error("Message with role and content is required");
		}

		const messageWithTimestamp = {
			...message,
			timestamp: new Date().toISOString()
		};

		await db
			.collection("Users")
			.doc(userId)
			.collection("Feedback")
			.doc(feedbackId)
			.update({
				"aiConversation.messages": firebase.firestore.FieldValue.arrayUnion(messageWithTimestamp),
				"aiConversation.status": "in_progress",
				"aiConversation.startedAt": firebase.firestore.FieldValue.serverTimestamp(),
				updatedAt: firebase.firestore.FieldValue.serverTimestamp()
			});

		return true;
	} catch (error) {
		console.error("[firestore][addFeedbackMessage] - Error adding message:", error);
		throw error;
	}
}

/**
 * Marks the feedback AI conversation as skipped
 * @param {string} userId - The unique identifier for the user
 * @param {string} feedbackId - The feedback document ID
 * @returns {Promise<boolean>} Success status
 * @path Users/{userId}/Feedback/{feedbackId}
 */
export async function skipFeedbackConversation(userId, feedbackId) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}

		if (!feedbackId) {
			throw new Error("Feedback ID is required");
		}

		await db
			.collection("Users")
			.doc(userId)
			.collection("Feedback")
			.doc(feedbackId)
			.update({
				"aiConversation.status": "skipped",
				"aiConversation.completedAt": firebase.firestore.FieldValue.serverTimestamp(),
				updatedAt: firebase.firestore.FieldValue.serverTimestamp()
			});

		return true;
	} catch (error) {
		console.error("[firestore][skipFeedbackConversation] - Error skipping conversation:", error);
		throw error;
	}
}

/**
 * Writes prediction feedback from user (was prediction accurate?)
 * @param {string} userId - The unique identifier for the user
 * @param {Object} feedbackData - The feedback data
 * @param {string} feedbackData.animalId - Animal ID
 * @param {string} feedbackData.animalName - Animal name
 * @param {string} feedbackData.predictionType - Type: "Heat", "Birth", or "Lameness"
 * @param {string} feedbackData.predictionId - Reference to original prediction document
 * @param {number} feedbackData.probability - The predicted probability (0-1)
 * @param {number} feedbackData.thresholdAtTime - User's threshold when prediction was made
 * @param {boolean} feedbackData.wasAccurate - Whether the prediction was accurate
 * @param {number} feedbackData.originalPredictionTimestamp - When prediction was made
 * @returns {Promise<{success: boolean, feedbackId?: string, error?: string}>}
 * @path Users/{userId}/PredictionFeedback/{feedbackId}
 */
export async function writePredictionFeedback(userId, feedbackData) {
	try {
		if (!userId) {
			throw new Error("User ID is required");
		}
		if (!feedbackData.predictionType || !feedbackData.animalId) {
			throw new Error("Prediction type and animal ID are required");
		}

		const feedbackId = `${feedbackData.predictionType}_${feedbackData.animalId}_${Date.now()}`;

		const feedbackDoc = {
			feedbackId: feedbackId,
			animalId: feedbackData.animalId,
			animalName: feedbackData.animalName || "",
			predictionType: feedbackData.predictionType,
			predictionId: feedbackData.predictionId || null,
			probability: feedbackData.probability || 0,
			thresholdAtTime: feedbackData.thresholdAtTime || 0,
			wasAccurate: feedbackData.wasAccurate,
			feedbackTimestamp: firebase.firestore.FieldValue.serverTimestamp(),
			originalPredictionTimestamp: feedbackData.originalPredictionTimestamp || null,
		};

		await db
			.collection("Users")
			.doc(userId)
			.collection("PredictionFeedback")
			.doc(feedbackId)
			.set(feedbackDoc);

		return { success: true, feedbackId: feedbackId };
	} catch (error) {
		console.error("[firestore][writePredictionFeedback] - Error:", error);
		return { success: false, error: error.message };
	}
}

/**
 * Gets prediction feedback for a specific animal and prediction type
 * @param {string} userId - The unique identifier for the user
 * @param {string} predictionType - Type: "Heat", "Birth", or "Lameness"
 * @param {string} animalId - Animal ID
 * @param {string} predictionId - Optional prediction document ID
 * @returns {Promise<Object|null>} Feedback data or null if not found
 * @path Users/{userId}/PredictionFeedback
 */
export async function getPredictionFeedback(userId, predictionType, animalId, predictionId = null) {
	try {
		if (!userId || !predictionType || !animalId) {
			return null;
		}

		let query = db
			.collection("Users")
			.doc(userId)
			.collection("PredictionFeedback")
			.where("predictionType", "==", predictionType)
			.where("animalId", "==", animalId);

		if (predictionId) {
			query = query.where("predictionId", "==", predictionId);
		}

		const snapshot = await query.limit(1).get();

		if (snapshot.empty) {
			return null;
		}

		return snapshot.docs[0].data();
	} catch (error) {
		console.error("[firestore][getPredictionFeedback] - Error:", error);
		return null;
	}
}

/**
 * Gets prediction accuracy statistics for a user
 * @param {string} userId - The unique identifier for the user
 * @param {string} predictionType - Optional: filter by type ("Heat", "Birth", or "Lameness")
 * @returns {Promise<Object>} Stats with total, accurate, inaccurate counts and accuracy rates
 * @path Users/{userId}/PredictionFeedback
 */
export async function getPredictionAccuracyStats(userId, predictionType = null) {
	try {
		if (!userId) {
			return {
				total: 0,
				accurate: 0,
				inaccurate: 0,
				accuracyRate: null,
				byType: {},
			};
		}

		let query = db
			.collection("Users")
			.doc(userId)
			.collection("PredictionFeedback");

		if (predictionType) {
			query = query.where("predictionType", "==", predictionType);
		}

		const snapshot = await query.get();

		if (snapshot.empty) {
			return {
				total: 0,
				accurate: 0,
				inaccurate: 0,
				accuracyRate: null,
				byType: {
					Heat: { total: 0, accurate: 0, accuracyRate: null },
					Birth: { total: 0, accurate: 0, accuracyRate: null },
					Lameness: { total: 0, accurate: 0, accuracyRate: null },
				},
			};
		}

		const stats = {
			total: 0,
			accurate: 0,
			inaccurate: 0,
			byType: {
				Heat: { total: 0, accurate: 0 },
				Birth: { total: 0, accurate: 0 },
				Lameness: { total: 0, accurate: 0 },
			},
		};

		snapshot.forEach((doc) => {
			const data = doc.data();
			stats.total++;
			if (data.wasAccurate) {
				stats.accurate++;
			} else {
				stats.inaccurate++;
			}

			if (stats.byType[data.predictionType]) {
				stats.byType[data.predictionType].total++;
				if (data.wasAccurate) {
					stats.byType[data.predictionType].accurate++;
				}
			}
		});

		stats.accuracyRate = stats.total > 0 ? (stats.accurate / stats.total) * 100 : null;

		// Calculate per-type accuracy rates
		Object.keys(stats.byType).forEach((type) => {
			const typeStats = stats.byType[type];
			typeStats.accuracyRate = typeStats.total > 0
				? (typeStats.accurate / typeStats.total) * 100
				: null;
		});

		return stats;
	} catch (error) {
		console.error("[firestore][getPredictionAccuracyStats] - Error:", error);
		return {
			total: 0,
			accurate: 0,
			inaccurate: 0,
			accuracyRate: null,
			byType: {},
		};
	}
}
